import MuiTableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import { Block, Delete, Edit, LockOpen } from "@mui/icons-material";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import Text from "@commons/Typography/Text";
import { isEmpty } from "@utils/formatters";
import type { actionsType } from "@typings/components/table";

type ColumnFormatter<T> = (value: T[keyof T], row: T) => React.ReactNode;

interface TableBodyProps<T extends { id: string }> {
  rows: T[];
  columns: string[];
  actions?: actionsType;
  columnFormatters?: Record<string, ColumnFormatter<T>>;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  onBlock?: (row: T) => void;
  onUnblock?: (row: T) => void;
}

function TableBody<T extends { id: string }>({
  rows,
  columns,
  actions = [],
  columnFormatters = {},
  onEdit,
  onDelete,
  onView,
  onBlock,
  onUnblock,
}: TableBodyProps<T>) {
  if (!rows?.length) return <MuiTableBody />;

  const actionHandlers = {
    edit: onEdit,
    delete: onDelete,
    view: onView,
    block: onBlock,
    unblock: onUnblock,
  };

  const actionIcons = {
    edit: <Edit />,
    delete: <Delete />,
    view: <FindInPageOutlinedIcon />,
    block: <Block />,
    unblock: <LockOpen />,
  };

  const actionColors = {
    edit: "bg-primary",
    delete: "bg-red-700",
    view: "bg-primary",
    block: "bg-secondary",
    unblock: "bg-secondary",
  };

  return (
    <MuiTableBody>
      {rows.map((row, idx) => (
        <TableRow key={row.id} className={idx % 2 === 0 ? "bg-muted" : ""}>
          {columns.map((col) => {
            const formatter = columnFormatters[col];
            const value = row[col as keyof T];

            return (
              <TableCell key={col} size="small" align="left">
                {formatter ? (
                  formatter(value, row)
                ) : isEmpty(value) ? (
                  <span className="text-gray-400">—</span>
                ) : (
                  <Text size="sm" className="font-roboto whitespace-nowrap">
                    {String(value)}
                  </Text>
                )}
              </TableCell>
            );
          })}

          {actions.length > 0 && (
            <TableCell size="small" align="left">
              <div className="flex gap-3">
                {actions.map((action) => (
                  <IconButton
                    key={action}
                    data-testid={`table-action-${action}`}
                    className={`${actionColors[action]} text-white p-1.5`}
                    onClick={() => actionHandlers[action]?.(row)}
                  >
                    {actionIcons[action]}
                  </IconButton>
                ))}
              </div>
            </TableCell>
          )}
        </TableRow>
      ))}
    </MuiTableBody>
  );
}

export default TableBody;
